import React, { useEffect, useReducer } from 'react';
import { Add, Cancel } from '@mui/icons-material';
import {
  Button, Chip, Divider, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import {
  addUpgradeToBuilding,
  getAddableUpgrades,
  getBuildingUpgrades,
  removeUpgradeFromBuilding,
} from 'shared/api/upgrades';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { viewPath as tenantSearchPath } from 'shared/routes/tenant';
import { useRequest } from 'shared/hooks';
import { StaticSearchableSelect } from 'shared/components/select/SearchableSelects';
import StyledStack from 'shared/components/styled/stacks';
import { formatBuildingUpgrade } from '../../shared/utils/formatters';

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'setUpgrades':
      return {
        ...state,
        currentUpgrades: payload.current || [],
        addableUpgrades: payload.addable || [],
      };
    case 'newPlan':
      return {
        ...state,
        newPlan: payload,
      };
    case 'refresh':
      // Dispatched after adding or removing an upgrade.
      // Counter changing causes the upgrade fetch effect to rerun.
      // This loads the new current and available upgrades.
      return {
        ...state,
        counter: state.counter + 1,
        newPlan: undefined,
      };
    default:
      return state;
  }
}

/**
 * @param {Object<string, *>} buildingState - The building state object
 */
export default function UpgradeManager({
  buildingState: {
    data: { id },
    editEnabled,
  },
}) {
  const {
    request,
    isLoading,
  } = useRequest();

  const [state, dispatch] = useReducer(reducer, {
    currentUpgrades: [],
    addableUpgrades: [],
    counter: 0,
    newPlan: undefined,
  });

  const {
    currentUpgrades,
    addableUpgrades,
    newPlan,
    counter,
  } = state;

  const removeUpgrade = async (propertyPlanId) => {
    const response = await request(removeUpgradeFromBuilding(propertyPlanId), 'Removed upgrade');
    if (response) {
      dispatch({ action: 'refresh' });
    }
  };

  const addUpgrade = async () => {
    const response = await request(addUpgradeToBuilding(id, newPlan.id), 'Added upgrade');
    if (response) {
      dispatch({ action: 'refresh' });
    }
  };

  useEffect(() => {
    (async () => {
      const [currentResponse, addableResponse] = await Promise.all([
        request(getBuildingUpgrades(id)),
        request(getAddableUpgrades(id)),
      ]);

      if (currentResponse === undefined) return;

      dispatch({
        action: 'setUpgrades',
        payload: {
          addable: addableResponse,
          current: currentResponse,
        },
      });
    })();
  }, [id, counter, request]);

  return (
    <CardSection>
      <CardControls
        disabled={isLoading}
        editEnabled={editEnabled}
      />
      <BoxWrapper>
        <Grid size={12} container sx={{ justifyContent: 'space-between' }}>
          <GridWrapper
            size={{ xl: 4, md: 6, xs: 12 }}
            title="Current Upgrades"
          >
            <StyledStack divider={<Divider flexItem />} sx={{ marginTop: '5px' }}>
              {(currentUpgrades.length === 0)
                && (
                  <Typography>No upgrades</Typography>
                )}
              {currentUpgrades.map((upgrade) => (
                <Grid
                  container
                  size={12}
                  key={upgrade.id}
                  sx={{
                    paddingY: 1, paddingX: 2, justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <Grid>
                    <Typography>{`${formatBuildingUpgrade(upgrade)}`}</Typography>
                  </Grid>
                  {editEnabled && (
                    <Grid>
                      {upgrade.sub_count === 0 ? (
                        <TooltipIconButton
                          message="Remove upgrade"
                          className="list-group-item-icon clear"
                          onClick={() => removeUpgrade(upgrade.id)}
                          icon={Cancel}
                        />

                      ) : (
                        <Chip
                          component="a"
                          target="_blank"
                          color={BUTTON_COLORS.primary}
                          size="small"
                          label={`${upgrade.sub_count} ${upgrade.sub_count > 1
                            ? 'subscriptions' : 'subscription'}`}
                          href={`${tenantSearchPath}?building_id=${id}&plan_id=${upgrade.plan_id}`}
                          clickable
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              ))}
            </StyledStack>
          </GridWrapper>
          {editEnabled && (
            <GridWrapper
              size={{ md: 6, xs: 12 }}
              title="Add Upgrades"
            >
              <Grid container sx={{ justifyContent: 'start', alignItems: 'center' }} spacing={1}>
                <Grid size={{ xs: 12, md: 9 }}>
                  <StaticSearchableSelect
                    onChange={(value) => dispatch({
                      action: 'newPlan',
                      payload: value,
                    })}
                    label="Upgrades"
                    staticOptions={addableUpgrades}
                    getOptionLabel={formatBuildingUpgrade}
                    getOptionValue={(upgrade) => upgrade.id}
                    selectedOption={newPlan}
                    width="100%"
                    placeholder="Select upgrade..."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <Button
                    disabled={!newPlan}
                    startIcon={<Add />}
                    variant={VARIANTS.contained}
                    onClick={addUpgrade}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </GridWrapper>
          )}
        </Grid>
      </BoxWrapper>
    </CardSection>
  );
}
